import { useState, useCallback, useRef } from "react";
import { V } from "../theme.js";
import { OptBtn, Modal } from "../ui/components.jsx";

const LS_KEY = "pixalloy_actions";

function loadActions() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
}
function saveActions(list) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch {}
}

/**
 * useActions — hook for recording and playing back command macros.
 *
 * Returns:
 *   recording    boolean
 *   startRecord  () => void
 *   stopRecord   (name) => void — saves to localStorage
 *   cancelRecord () => void
 *   recordCmd    (type, payload) => void — call on every pc() while recording
 *   actions      array of { name, commands: [{type,payload}] }
 *   playAction   (idx, pc, fu) => void — replays commands
 *   deleteAction (idx) => void
 */
export function useActions() {
  const [recording, setRecording] = useState(false);
  const recordingRef = useRef(false);
  const [actions, setActions] = useState(() => loadActions());
  const bufferRef = useRef([]);

  const startRecord = useCallback(() => {
    bufferRef.current = [];
    recordingRef.current = true;
    setRecording(true);
  }, []);

  const stopRecord = useCallback((name) => {
    const cmds = bufferRef.current;
    recordingRef.current = false;
    setRecording(false);
    if (cmds.length === 0) return;
    const newActions = [...loadActions(), { name: name || `Action ${Date.now()}`, commands: cmds }];
    saveActions(newActions);
    setActions(newActions);
    bufferRef.current = [];
  }, []);

  const cancelRecord = useCallback(() => {
    bufferRef.current = [];
    recordingRef.current = false;
    setRecording(false);
  }, []);

  const recordCmd = useCallback((type, payload) => {
    if (!recordingRef.current) return;
    // Skip undo/redo and stroke on/off from recording
    if (type === 1 || type === 2 || type === 7 || type === 8) return;
    bufferRef.current.push({ type, payload: payload ? JSON.parse(JSON.stringify(payload)) : {} });
  }, []);

  const playAction = useCallback((idx, pc, fu) => {
    const act = actions[idx];
    if (!act) return;
    for (const cmd of act.commands) {
      pc(cmd.type, cmd.payload);
    }
    fu();
  }, [actions]);

  const deleteAction = useCallback((idx) => {
    const newActions = actions.filter((_, i) => i !== idx);
    saveActions(newActions);
    setActions(newActions);
  }, [actions]);

  return { recording, recordingRef, startRecord, stopRecord, cancelRecord, recordCmd, actions, playAction, deleteAction };
}

/**
 * ActionsDialog — manage and replay saved actions.
 */
export function ActionsDialog({ open, onClose, actions, recording, startRecord, stopRecord, cancelRecord, playAction, deleteAction, pc, fu }) {
  const [newName, setNewName] = useState("");

  if (!open) return null;

  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:12 }}>
        Quick Actions
      </div>

      <div style={{ marginBottom:12 }}>
        <OptBtn accent onClick={() => { startRecord(); onClose(); }}>Record New Action</OptBtn>
        <span style={{ fontSize:10, color:V.ghost, marginLeft:8 }}>Dialog closes — perform actions, then stop recording</span>
      </div>

      {actions.length === 0 ? (
        <div style={{ fontSize:11, color:V.ghost, padding:"12px 0" }}>
          No saved actions yet. Click "Record" and perform some edits to create a reusable macro.
        </div>
      ) : (
        <div style={{ maxHeight:240, overflowY:"auto" }}>
          {actions.map((act, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px",
              borderRadius:4, marginBottom:2, background:V.panelActive,
              border:`1px solid ${V.panelBorder2}` }}>
              <span style={{ flex:1, fontSize:11, color:V.text }}>{act.name}</span>
              <span style={{ fontSize:9, color:V.ghost }}>{act.commands.length} steps</span>
              <OptBtn onClick={() => { playAction(i, pc, fu); onClose(); }}>Play</OptBtn>
              <span onClick={() => deleteAction(i)}
                style={{ fontSize:10, color:V.ghost, cursor:"pointer", padding:"2px 4px" }}>✕</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:"flex", gap:6, justifyContent:"flex-end", marginTop:12 }}>
        <OptBtn onClick={onClose}>Close</OptBtn>
      </div>
    </Modal>
  );
}
